import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Activity, Award, Calendar, TrendingDown, Plus, Receipt } from "lucide-react";import { inr } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { adminUrl } from "@/lib/adminPath";

/**
 * Embedded inside the Admin Member-Edit modal as the "Visits" tab.
 * Pure read-only — shows visit timeline + stats for this member. Click
 * "Log New Visit" to jump into the Admin Visits page filtered to this
 * member (the existing /admin/visits flow handles creation).
 */
export function MemberVisitsPanel({ memberId, membership }) {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!memberId) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/members/${memberId}/visit-stats`).catch(() => null),
      api.get(`/members/${memberId}/visits?limit=50`).catch(() => null),
    ]).then(([s, v]) => {
      if (!alive) return;
      setStats(s?.data || null);
      setVisits(v?.data?.items || []);
    }).catch((e) => toast.error(apiErr(e, "Failed to load visits")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [memberId]);

  if (loading) {
    return <div className="py-12 text-center text-[#5C6B7A]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total Visits" value={stats?.total_visits ?? 0} icon={Activity} />
        <StatTile label="Last Visit" value={stats?.last_visit_date ? new Date(stats.last_visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"} icon={Calendar} />
        <StatTile label="Total Saved" value={inr(stats?.total_savings ?? 0)} icon={TrendingDown} tone="gold" />
        <StatTile label="Avg / month" value={(stats?.avg_monthly_visits ?? 0).toString()} icon={Receipt} />
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-[#0F2540]">Visit Timeline</h4>
        <button
          onClick={() => nav(adminUrl(`/visits?member_id=${memberId}`))}
          data-testid="member-visits-open-admin"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E4F87] text-white rounded-xl text-xs font-semibold hover:bg-[#0F2540]"
        >
          <Plus className="h-3.5 w-3.5" /> Log New Visit
        </button>
      </div>

      {visits.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E1E8F0] p-8 text-center" data-testid="member-visits-empty">
          <Activity className="h-8 w-8 text-[#1E4F87] opacity-40 mx-auto mb-2" />
          <p className="text-sm text-[#5C6B7A]">No visits logged yet for {membership?.full_name || "this member"}.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#E1E8F0] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FC] text-[#5C6B7A] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Department · Type</th>
                <th className="text-left px-4 py-3">Benefits Redeemed</th>
                <th className="text-right px-4 py-3">Bill</th>
                <th className="text-right px-4 py-3">Saved</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((v) => (
                <tr key={v.visit_id} className="border-t border-[#E1E8F0]" data-testid={`member-visit-row-${v.visit_id}`}>
                  <td className="px-4 py-3 text-[#0F2540] whitespace-nowrap">
                    {new Date(v.visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-3 text-[#0F2540]">
                    <div>{v.department}</div>
                    <div className="text-xs text-[#5C6B7A]">{v.visit_type}{v.doctor_name ? ` · ${v.doctor_name}` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    {(v.benefits_applied || []).length === 0 ? (
                      <span className="text-xs text-[#5C6B7A]">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[260px]">
                        {(v.benefits_applied || []).map((b, idx) => (
                          <span key={idx} title={`Saved ₹${b.savings || 0}`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#FAF1DC] text-[#8C6A00] border border-[#F0D58A] rounded-full px-2 py-0.5">
                            <Award className="h-2.5 w-2.5" />
                            {b.title}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-[#0F2540]">{inr(v.bill_amount || 0)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1F7A3D]">{inr(v.total_savings || 0)}</td>
                  <td className="px-4 py-3 text-[#5C6B7A]">{v.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Embedded inside the Admin Member-Edit modal as the "Benefit Wallet" tab.
 * Shows the same wallet data the customer sees in their dashboard so admins
 * can confirm benefit balances during phone support.
 */
export function MemberBenefitsPanel({ memberId }) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!memberId) return;
    let alive = true;
    setLoading(true);
    api.get(`/members/${memberId}/benefits`)
      .then((r) => { if (alive) setWallet(r.data); })
      .catch((e) => { if (alive) toast.error(apiErr(e, "Failed to load benefits")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [memberId]);

  if (loading) {
    return <div className="py-12 text-center text-[#5C6B7A]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }
  if (!wallet || (wallet.benefits || []).length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E1E8F0] p-8 text-center" data-testid="member-benefits-empty">
        <Award className="h-8 w-8 text-[#C89B3C] opacity-50 mx-auto mb-2" />
        <p className="text-sm text-[#5C6B7A]">No benefits attached to this plan yet.</p>
        <p className="text-xs text-[#5C6B7A] mt-1">Configure benefits at Admin → Plan Benefits and attach them to {wallet?.plan_name || "this plan"}.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-[#0F2540]">Benefit Wallet</h4>
          <p className="text-xs text-[#5C6B7A]">{wallet.plan_name}</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-[#5C6B7A] uppercase tracking-wider">Total Saved</div>
          <div className="font-display text-xl font-bold text-[#1F7A3D]">{inr(wallet.total_savings || 0)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {wallet.benefits.map((b) => {
          const used = b.used_count || 0;
          const total = b.total_allowed;
          const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
          return (
            <div key={b.benefit_id} className="rounded-2xl border border-[#E1E8F0] bg-white p-4" data-testid={`member-benefit-${b.benefit_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-[#0F2540]">{b.title}</div>
                  <div className="text-xs text-[#5C6B7A] mt-0.5">
                    {b.category} · {b.reset_period}
                    {b.benefit_type === "discount" && b.discount_value > 0 && (
                      <> · {b.discount_type === "percent" ? `${b.discount_value}%` : `₹${b.discount_value}`} off</>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#5C6B7A]">Saved</div>
                  <div className="text-sm font-bold text-[#1F7A3D]">{inr(b.savings_generated || 0)}</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-xs text-[#5C6B7A]">
                  <span>{total == null ? "Unlimited" : `${used} of ${total} used`}</span>
                  {total != null && <span>{b.remaining_count} left</span>}
                </div>
                <div className="mt-1 h-2 bg-[#F2F2F2] rounded-full overflow-hidden">
                  <div className="h-full bg-[#1E4F87]" style={{ width: `${total == null ? 0 : pct}%` }} />
                </div>
                {b.last_used_at && (
                  <div className="text-[10px] text-[#5C6B7A] mt-1">Last used · {new Date(b.last_used_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone = "navy" }) {
  const toneCls = tone === "gold"
    ? "bg-[#FAF1DC] text-[#8C6A00] border-[#F0D58A]"
    : "bg-[#EAF1FA] text-[#1E4F87] border-[#E1E8F0]";
  return (
    <div className={`rounded-2xl border p-3 flex items-center gap-3 ${toneCls}`}>
      <Icon className="h-5 w-5" />
      <div>
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
        <div className="font-display text-lg font-bold text-[#0F2540]">{value}</div>
      </div>
    </div>
  );
}
